import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Pagination, Select, Spin, message } from "antd"
import { useDispatch, useSelector } from "react-redux"
import PageHeader from "../../components/Common/PageHeader"
import EmptyState from "../../components/Common/EmptyState"
import { organizationApi } from "../../Apis/organizationApi"
import {
  parseOrganizationApiError,
  parseOrganizationGenerationsResponse,
  formatFeatureLabel,
  getAllowedPlanFeaturesFromOverview,
  getOrganizationGenerationMedia,
} from "../../utils/organizationApiHelpers"
import {
  fetchOrganizationMembers,
  fetchOrganizationOverview,
} from "../../ReduxToolKit/Slice/OrganizationSlice"
import OrganizationGenerationDetailModal from "../../components/Organization/OrganizationGenerationDetailModal"
import { translations } from "../../utils/Translation/staticTranslation"
import { getAppLanguage } from "../../utils/getAppLanguage"
import "../../styles/Pages/organization.css"

const PAGE_SIZE = 20
const SUCCESS_STATUSES = new Set(["success", "completed"])

const OrganizationGenerations = () => {
  const dispatch = useDispatch()
  const { overview, members } = useSelector((state) => state.organization)
  const lang = getAppLanguage()
  const t = translations[lang]?.organizationPage?.generationsPage

  const [loading, setLoading] = useState(true)
  const [generations, setGenerations] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [memberFilter, setMemberFilter] = useState("all")
  const [featureFilter, setFeatureFilter] = useState("all")
  const [selectedGeneration, setSelectedGeneration] = useState(null)

  const allowedPlanFeatures = useMemo(
    () => getAllowedPlanFeaturesFromOverview(overview),
    [overview],
  )

  const membersWithDashboard = useMemo(
    () =>
      (members || []).filter(
        (m) =>
          m.dashboard_user_id != null &&
          String(m.status || "").toLowerCase() === "active",
      ),
    [members],
  )

  const fetchGenerations = useCallback(async () => {
    setLoading(true)
    try {
      const params = { page, page_size: PAGE_SIZE }
      if (featureFilter && featureFilter !== "all") {
        params.feature = featureFilter
      }
      if (memberFilter && memberFilter !== "all") {
        params.dashboard_user_id = memberFilter
      }
      const response = await organizationApi.getGenerations(params)
      const parsed = parseOrganizationGenerationsResponse(response)
      const successfulGenerations = (parsed.results || []).filter((gen) =>
        SUCCESS_STATUSES.has(String(gen?.gen_status || "").toLowerCase()),
      )
      setGenerations(successfulGenerations)
      setTotal(successfulGenerations.length)
    } catch (error) {
      message.error(parseOrganizationApiError(error, lang))
      setGenerations([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [page, featureFilter, memberFilter, lang])

  useEffect(() => {
    dispatch(fetchOrganizationOverview())
    dispatch(fetchOrganizationMembers())
  }, [dispatch])

  useEffect(() => {
    fetchGenerations()
  }, [fetchGenerations])

  const handleMemberChange = (value) => {
    setMemberFilter(value)
    setPage(1)
  }

  const handleFeatureChange = (value) => {
    setFeatureFilter(value)
    setPage(1)
  }

  const formatDate = (iso) => {
    if (!iso) return "—"
    try {
      return new Date(iso).toLocaleString(lang === "ar" ? "ar-SA" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch {
      return iso
    }
  }

  const statusClass = (status) => {
    const s = String(status || "").toLowerCase()
    if (s === "completed" || s === "success") return "active"
    if (s === "failed" || s === "error") return "rejected"
    return "pending"
  }

  return (
    <div className="organization-team-page org-generations-page">
      <PageHeader title={t?.pageTitle} />

      <div className="org-generations-filters">
        <div className="org-filter-field">
          <label className="org-filter-label">{t?.filterMember}</label>
          <Select
            className="org-filter-select"
            value={memberFilter}
            onChange={handleMemberChange}
            options={[
              { value: "all", label: t?.allMembers },
              ...membersWithDashboard.map((m) => ({
                value: String(m.dashboard_user_id),
                label: m.email,
              })),
            ]}
          />
        </div>
        <div className="org-filter-field">
          <label className="org-filter-label">{t?.filterFeature}</label>
          <Select
            className="org-filter-select"
            value={featureFilter}
            onChange={handleFeatureChange}
            options={[
              { value: "all", label: t?.allFeatures },
              ...allowedPlanFeatures.map((f) => ({
                value: f,
                label: formatFeatureLabel(f, lang),
              })),
            ]}
          />
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-64">
          <Spin size="large" />
        </div>
      ) : generations.length === 0 ? (
        <EmptyState
          title={t?.emptyTitle}
          description={t?.emptyDescription}
        />
      ) : (
        <>
          <div className="org-generations-grid">
            {generations.map((gen) => {
              const { outputUrl } = getOrganizationGenerationMedia(gen)
              return (
              <article key={gen.id} className="org-generation-card">
                <button
                  type="button"
                  className="org-generation-image-link"
                  onClick={() => setSelectedGeneration(gen)}
                  aria-label={t?.viewDetails}
                >
                  {outputUrl ? (
                    <img
                      src={outputUrl}
                      alt={gen.prompt || t?.generationAlt}
                      className="org-generation-image"
                      loading="lazy"
                    />
                  ) : (
                    <div className="org-generation-image org-generation-image--placeholder">
                      {t?.noImage}
                    </div>
                  )}
                  <span className="org-generation-view-overlay">
                    {t?.viewDetails}
                  </span>
                </button>
                <div className="org-generation-body">
                  <div className="org-generation-meta">
                    <span className="org-generation-email">
                      {gen.member_email}
                    </span>
                    <span className={`status ${statusClass(gen.gen_status)}`}>
                      {gen.gen_status || "—"}
                    </span>
                  </div>
                  <span className="org-generation-feature">
                    {formatFeatureLabel(gen.feature, lang)}
                  </span>
                  {gen.prompt ? (
                    <p className="org-generation-prompt" title={gen.prompt}>
                      {gen.prompt}
                    </p>
                  ) : null}
                  <span className="org-generation-date">
                    {formatDate(gen.time_created)}
                  </span>
                </div>
              </article>
            )})}
          </div>

          {total > PAGE_SIZE ? (
            <div className="d-flex justify-content-end mt-24">
              <Pagination
                current={page}
                pageSize={PAGE_SIZE}
                total={total}
                showSizeChanger={false}
                onChange={(p) => setPage(p)}
                showTotal={(tot, range) =>
                  t?.paginationTotal
                    ?.replace("{from}", String(range[0]))
                    ?.replace("{to}", String(range[1]))
                    ?.replace("{total}", String(tot)) ||
                  `${range[0]}-${range[1]} of ${tot}`
                }
              />
            </div>
          ) : null}
        </>
      )}

      <OrganizationGenerationDetailModal
        open={Boolean(selectedGeneration)}
        generation={selectedGeneration}
        onClose={() => setSelectedGeneration(null)}
      />
    </div>
  )
}

export default OrganizationGenerations
